import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useMountedRef } from "./use-mounted";

type Eff<Parameters extends unknown[], ReturnType> = (...args: Parameters) => Promise<ReturnType>;

export function useTrackedEffect<Parameters extends unknown[], Result>(
  eff: Eff<Parameters, Result>,
  opts: { onlyOnePending: boolean },
): readonly [boolean, (...params: Parameters) => Promise<Result>] {
  const [isPending, setPending] = useState(false);

  const isPendingRef = useRef(isPending);
  isPendingRef.current = isPending;

  const isMountedRef = useMountedRef();

  const wrappedEffect = useCallback(
    (...params: Parameters) => {
      if (opts.onlyOnePending && isPendingRef.current) {
        return Promise.reject<Result>(new Error("Only one pending effect is allowed."));
      } else {
        setPending(true);

        return eff(...params).finally(() => {
          if (isMountedRef.current) {
            setPending(false);
          }
        });
      }
    },
    [eff, opts.onlyOnePending],
  );

  return [isPending, wrappedEffect] as const;
}
